import React, { useRef, useEffect } from 'react';
import { useEsteiras } from '../../../hooks/useEsteiras';
import './ActiveSessionsConflictModal.css';

interface ActiveSession {
  id: string;
  collaboratorId: string;
  collaboratorName: string;
  sector: string;
}

/** Quem sai do veículo por não ter a especialidade da esteira destino (at-62). */
interface ColaboradorInelegivel {
  collaboratorId: string;
  collaboratorName: string | null;
}

interface ActiveSessionsConflictModalProps {
  isOpen: boolean;
  activeSessions: ActiveSession[];
  actionType: 'STATUS_CHANGE' | 'COLLABORATOR_CHANGE' | 'APPROVAL_CHANGE';
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * at-62: colaboradores que serão DESVINCULADOS por não atenderem a esteira destino.
   *
   * Vem no mesmo payload do 409 que traz as sessões, de propósito: os dois efeitos acontecem
   * juntos e o Chefe precisa aceitá-los numa confirmação só, em vez de encadear dois modais.
   */
  inelegiveis?: ColaboradorInelegivel[];
  /** Nome da esteira destino, para a frase dizer POR QUE eles saem. */
  esteiraDestinoNome?: string;
}

const ActiveSessionsConflictModal: React.FC<ActiveSessionsConflictModalProps> = ({
  isOpen,
  activeSessions,
  actionType,
  onConfirm,
  onCancel,
  inelegiveis = [],
  esteiraDestinoNome,
}) => {
  const { labelDe } = useEsteiras();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  let message = 'Atenção: A alteração de esteira deste veículo irá encerrar as seguintes sessões de trabalho ativas. Deseja forçar o encerramento e prosseguir?';
  if (actionType === 'COLLABORATOR_CHANGE') {
    message = 'Atenção: A alteração ou remoção do colaborador responsável deste veículo irá encerrar as seguintes sessões de trabalho ativas. Deseja forçar o encerramento e prosseguir?';
  } else if (actionType === 'APPROVAL_CHANGE') {
    message = 'Atenção: A aprovação de mudança de esteira deste veículo irá encerrar as seguintes sessões de trabalho ativas. Deseja forçar o encerramento e prosseguir?';
  }

  const temInelegiveis = inelegiveis.length > 0;
  // at-62: quando há inelegíveis, o desvínculo é o efeito principal — pode não haver sessão
  // ativa nenhuma, e nesse caso a mensagem acima falaria de uma lista vazia.
  const titulo = temInelegiveis
    ? 'Colaboradores sem a especialidade desta esteira'
    : 'Sessões Ativas em Execução';

  return (
    <div className="active-sessions-conflict-modal-overlay">
      <div className="active-sessions-conflict-modal-content" ref={modalRef}>
        <div className="conflict-modal-header">
          <h3>{titulo}</h3>
        </div>
        <div className="conflict-modal-body">
          {temInelegiveis && (
            <>
              {/* Frase de gênero NEUTRO ("sai do veículo"): o sistema não sabe o gênero de quem
                  está no cadastro, e "Maria será desvinculado" lê como defeito. */}
              <p className="conflict-modal-message">
                {inelegiveis.length === 1
                  ? `${inelegiveis[0].collaboratorName ?? 'O colaborador'} não tem a especialidade`
                  : `${inelegiveis.length} colaboradores não têm a especialidade`}{' '}
                {esteiraDestinoNome ? `da esteira ${esteiraDestinoNome}` : 'da esteira de destino'} e{' '}
                {inelegiveis.length === 1 ? 'sai' : 'saem'} do veículo. As atividades em aberto são
                encerradas e as horas trabalhadas vão para aprovação. Quem atende a esteira nova
                permanece no veículo.
              </p>
              <div className="conflict-sessions-list">
                {inelegiveis.map((colaborador) => (
                  <div key={colaborador.collaboratorId} className="conflict-session-card">
                    <div className="conflict-session-collaborator">
                      <strong>Sai do veículo:</strong> {colaborador.collaboratorName ?? 'Colaborador'}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {(!temInelegiveis || activeSessions.length > 0) && (
            <p className="conflict-modal-message">
              {temInelegiveis
                ? 'As seguintes atividades em andamento serão encerradas:'
                : message}
            </p>
          )}

          <div className="conflict-sessions-list">
            {activeSessions.map((session) => (
              <div key={session.id} className="conflict-session-card">
                <div className="conflict-session-collaborator">
                  <strong>Colaborador:</strong> {session.collaboratorName}
                </div>
                <div className="conflict-session-sector">
                  <strong>Setor/Esteira:</strong> {labelDe(session.sector) || session.sector}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="conflict-modal-footer">
          <div className="CtaBigEditar cancel" onClick={onCancel}>
            <button className="CustomFileCancel" type="button">
              Cancelar
            </button>
          </div>
          <button
            className="CtaBigEditar save-danger"
            type="button"
            onClick={onConfirm}
          >
            {temInelegiveis ? 'Sim, prosseguir' : 'Sim, forçar encerramento'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveSessionsConflictModal;
